import React, { useState, useEffect, createContext, useContext } from 'react';
import { User, LoginCredentials, RegistrationRequest } from '../types';
import { authService } from '../services/authService';
import { initialRegistrationRequests } from '../data/mockData';

interface AuthContextType {
    isAuthenticated: boolean;
    user: User | null;
    loading: boolean;
    registrationRequests: RegistrationRequest[];
    login: (credentials: LoginCredentials) => Promise<void>;
    register: (request: RegistrationRequest) => Promise<void>;
    logout: () => void;
    approveRequest: (request: RegistrationRequest) => Promise<void>;
    rejectRequest: (requestId: string) => void;
    updateProfile: (updatedUser: User) => Promise<void>;
    usersList: User[];
    updateUser: (updatedUser: User) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [registrationRequests, setRegistrationRequests] = useState<RegistrationRequest[]>(initialRegistrationRequests);

    const [usersList, setUsersList] = useState<User[]>([]);

    useEffect(() => {
        const initAuth = async () => {
            const storedUser = authService.getCurrentUser();
            if (storedUser) {
                setUser(storedUser);

                // Only fetch admin data if user is authorized
                if (storedUser.role === 'admin' || storedUser.role === 'superadmin') {
                    try {
                        // Initialize users list
                        const users = await authService.getUsers();
                        setUsersList(users);

                        // Fetch registration requests
                        const requests = await authService.getRegistrationRequests();

                        if (requests.length === 0 && initialRegistrationRequests.length > 0) {
                            setRegistrationRequests(initialRegistrationRequests);
                        } else {
                            setRegistrationRequests(requests);
                        }
                    } catch (error) {
                        console.log("Failed to fetch admin data (expected if rules deny):", error);
                    }
                }
            }



            setLoading(false);
        };
        initAuth();
    }, []);



    const login = async (credentials: LoginCredentials) => {
        try {
            const user = await authService.login(credentials.email, credentials.password);
            setUser(user);
        } catch (error) {
            throw error;
        }
    };

    const logout = () => {
        authService.logout();
        setUser(null);
        localStorage.removeItem('sbajUser'); // Remove persisted user
    };

    const register = async (request: RegistrationRequest) => {
        await authService.submitRegistrationRequest(request);
        // Optionally update local registration requests state
        setRegistrationRequests(prev => [...prev, { ...request, id: Date.now().toString(), status: 'pending' }]);
    };

    const approveRequest = async (request: RegistrationRequest) => {
        if (!request.id) return;

        await authService.approveRequest(request);

        // Refresh users list
        const updatedUsers = await authService.getUsers();
        setUsersList(updatedUsers);

        // Update registration requests state
        setRegistrationRequests(prev => prev.map(r => r.id === request.id ? { ...r, status: 'approved' } : r));
    };

    const rejectRequest = async (requestId: string) => {
        await authService.rejectRequest(requestId);
        setRegistrationRequests(prev => prev.map(r => r.id === requestId ? { ...r, status: 'rejected' } : r));
    };

    const updateProfile = async (updatedUser: User) => {
        await authService.updateUser(updatedUser);
        setUser(updatedUser);

        // Refresh users list
        const updatedUsers = await authService.getUsers();
        setUsersList(updatedUsers);
        localStorage.setItem('sbajUser', JSON.stringify(updatedUser)); // Update persisted user
    };

    return (
        <AuthContext.Provider value={{
            user,
            loading,
            isAuthenticated: !!user,
            login,
            logout,
            register,
            registrationRequests,
            usersList,
            approveRequest,
            rejectRequest,
            updateProfile,
            updateUser: async (updatedUser: User) => {
                await authService.updateUser(updatedUser);
                const updatedUsers = await authService.getUsers();
                setUsersList(updatedUsers);
                // If the updated user is the current user, update local state too
                if (user && user.id === updatedUser.id) {
                    setUser(updatedUser);
                    localStorage.setItem('sbajUser', JSON.stringify(updatedUser));
                }
            }
        }}>
            {children}
        </AuthContext.Provider >
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) throw new Error('useAuth must be used within AuthProvider');
    return context;
};
